import {
  Anchor,
  Award,
  Briefcase,
  Building2,
  Cake,
  Calendar,
  Camera,
  Clock,
  Coffee,
  Crown,
  Flag,
  Folder,
  FolderOpen,
  Gamepad2,
  Heart,
  Home,
  MapPin,
  Music,
  Phone,
  Ruler,
  ShoppingBag,
  Smile,
  Sparkles,
  Star,
  Tag,
  Users,
} from "lucide-react";

export const TAG_COLORS = [
  { name: "Yellow", value: "#F5D300" },
  { name: "Cyan", value: "#00E5FF" },
  { name: "Blue", value: "#3B82F6" },
  { name: "Pink", value: "#FF3DCB" },
  { name: "Magenta", value: "#E91E63" },
  { name: "Red", value: "#FF5A5A" },
  { name: "Orange", value: "#FF8A1F" },
  { name: "Lime", value: "#B0FF3D" },
  { name: "Teal", value: "#00C2A8" },
  { name: "Violet", value: "#A66BFF" },
  { name: "White", value: "#E6E6E6" },
  { name: "Slate", value: "#7A8493" },
];

// keep old name for backwards-compat where used
export const FOLDER_COLORS = TAG_COLORS;

export const FOLDER_ICONS = {
  Folder,
  FolderOpen,
  Tag,
  MapPin,
  Home,
  Briefcase,
  Star,
  Heart,
  Users,
  Building2,
  Coffee,
  Phone,
  ShoppingBag,
  Cake,
  Clock,
  Calendar,
  Ruler,
  Anchor,
  Award,
  Smile,
  Music,
  Camera,
  Gamepad2,
  Crown,
  Sparkles,
  Flag,
};

export const FOLDER_ICON_LIST = Object.keys(FOLDER_ICONS);

export function FolderIcon({ name = "Folder", className = "h-4 w-4" }) {
  const Cmp = FOLDER_ICONS[name] || Folder;
  return <Cmp className={className} />;
}

export const DEFAULT_FOLDER_COLOR = "#F5D300";
export const DEFAULT_FOLDER_ICON = "Folder";
export const DEFAULT_TAG_COLOR = "#F5D300";

// ---------- Smart helpers ----------

/**
 * Pick the next palette colour that hasn't been used yet.
 * Falls back to a deterministic-but-rotating value once the palette is exhausted.
 */
export function pickNextColor(usedColors = [], seed = 0) {
  const used = new Set((usedColors || []).map((c) => (c || "").toUpperCase()));
  const fresh = TAG_COLORS.find((c) => !used.has(c.value.toUpperCase()));
  if (fresh) return fresh.value;
  const idx = ((seed || 0) + used.size) % TAG_COLORS.length;
  return TAG_COLORS[idx].value;
}

/**
 * Lightweight keyword → icon mapping. Each keyword is matched against the lowercased
 * folder name; the first match wins. Falls back to a deterministic icon based on
 * the name's char codes so naming "Hobbies" and "Hobby" stay consistent.
 */
const ICON_RULES = [
  { icon: "Cake", words: ["age", "birthday", "anniversary"] },
  { icon: "Users", words: ["gender", "family", "friend", "people", "team"] },
  { icon: "Ruler", words: ["size", "height", "tall", "short", "length", "scale"] },
  { icon: "Anchor", words: ["weight", "heavy"] },
  { icon: "Smile", words: ["attitude", "mood", "personality", "vibe", "feeling"] },
  { icon: "Sparkles", words: ["attribute", "trait", "trait", "style", "magic"] },
  { icon: "Crown", words: ["vip", "premium", "favourite", "favorite", "top"] },
  { icon: "Heart", words: ["love", "favourite", "favorite", "fav", "heart"] },
  { icon: "Music", words: ["music", "song", "audio", "playlist"] },
  { icon: "Camera", words: ["photo", "pic", "image", "shoot"] },
  { icon: "Gamepad2", words: ["game", "gaming", "play", "console"] },
  { icon: "Star", words: ["star", "rating", "rank"] },
  { icon: "Phone", words: ["phone", "call", "contact"] },
  { icon: "MapPin", words: ["place", "location", "area", "region", "city", "town"] },
  { icon: "Home", words: ["home", "house", "address"] },
  { icon: "Briefcase", words: ["work", "job", "business", "career", "office"] },
  { icon: "Building2", words: ["company", "client", "vendor", "building", "shop", "store"] },
  { icon: "Coffee", words: ["coffee", "food", "drink", "cafe"] },
  { icon: "ShoppingBag", words: ["shopping", "purchase", "buy", "order"] },
  { icon: "Calendar", words: ["date", "calendar", "schedule", "event"] },
  { icon: "Clock", words: ["time", "hour", "minute", "history"] },
  { icon: "Award", words: ["award", "achievement", "winner", "prize"] },
  { icon: "Flag", words: ["country", "nation", "region", "flag"] },
  { icon: "Tag", words: ["tag", "label", "category", "type"] },
];

export function suggestIconForName(name) {
  const lower = (name || "").toLowerCase().trim();
  if (!lower) return null;
  for (const rule of ICON_RULES) {
    if (rule.words.some((w) => lower.includes(w))) return rule.icon;
  }
  // Deterministic random fallback so the same name always picks the same icon
  let h = 0;
  for (let i = 0; i < lower.length; i += 1) h = (h * 31 + lower.charCodeAt(i)) | 0;
  const pool = FOLDER_ICON_LIST.filter((n) => n !== "Folder" && n !== "FolderOpen");
  return pool[Math.abs(h) % pool.length];
}
